import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router'
import { formatDistanceToNow, format } from 'date-fns'
import { ArrowLeft, CheckCircle2, ChevronDown, Mail, Send, Loader2 } from 'lucide-react'
import {
  useAdminFeedbackDetail,
  useAdminReply,
  useUpdateConversationStatus,
  useUpdateConversationPriority,
  useMarkAdminRead,
} from '@/hooks/useAdminFeedback'
import { useAdminFeedbackRealtime } from '@/hooks/useAdminFeedbackRealtime'
import { useAdminAuth } from '@/contexts/AdminAuthContext'
import { formatAdminSenderLabel } from '@/lib/utils/feedbackLabels'
import { StatusBadge, CategoryBadge, PriorityBadge } from './ConversationStatusBadge'
import { Badge } from '@/components/ui/badge'
import { PageHeader } from '@/components/shared/PageHeader'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import type { ConversationStatus, ConversationPriority } from '@hangarmax/types'

export function FeedbackThreadPage() {
  useAdminFeedbackRealtime()
  const { conversationId } = useParams<{ conversationId: string }>()
  const navigate = useNavigate()
  const { user: _user } = useAdminAuth()
  const { data, isLoading } = useAdminFeedbackDetail(conversationId)
  const replyMutation = useAdminReply()
  const statusMutation = useUpdateConversationStatus()
  const priorityMutation = useUpdateConversationPriority()
  const markReadMutation = useMarkAdminRead()
  const [replyText, setReplyText] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Auto-grow textarea to fit content, up to a generous max height.
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 320)}px`
  }, [replyText])

  const conversation = data?.conversation
  const messages = data?.messages ?? []
  const lastAdminMessageId = [...messages].reverse().find((m) => m.sender_role === 'admin')?.id

  // Mark as read on open
  useEffect(() => {
    if (conversationId && conversation?.admin_unread && conversation.admin_unread > 0) {
      markReadMutation.mutate(conversationId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, conversation?.admin_unread])

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages])

  const handleSendReply = () => {
    const body = replyText.trim()
    if (!body || !conversationId || replyMutation.isPending) return
    replyMutation.mutate({ conversationId, body })
    setReplyText('')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Cmd/Ctrl+Enter sends; plain Enter inserts a newline. Long admin replies
    // span paragraphs, so plain Enter as "send" is too easy to trigger by mistake.
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      handleSendReply()
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!conversation) {
    return (
      <div className="space-y-6">
        <PageHeader title="Conversation not found" description="" />
        <Button variant="outline" onClick={() => navigate('/feedback')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
      </div>
    )
  }

  const context = conversation.context as Record<string, unknown>

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/feedback')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <PageHeader
            title={conversation.subject || 'Feedback Thread'}
            description={`From ${conversation.profiles?.display_name ?? conversation.user_email}`}
          />
        </div>
        {conversation.profiles?.is_test === true && (
          <Badge variant="secondary" className="text-[10px]">
            Internal
          </Badge>
        )}
        {(conversation.user_open_threads ?? 0) > 0 && (
          <Badge
            variant="outline"
            className="text-[10px]"
            title="Other threads from this user that are not resolved or archived"
          >
            {conversation.user_open_threads} other open
          </Badge>
        )}
        {conversation.status !== 'resolved' && conversation.status !== 'archived' && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={statusMutation.isPending}>
                {statusMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                )}
                Mark Resolved
                <ChevronDown className="h-3 w-3 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onSelect={() =>
                  conversationId &&
                  statusMutation.mutate({ conversationId, status: 'resolved', notifyUser: false })
                }
              >
                <CheckCircle2 className="mr-2 h-4 w-4" /> Resolve (no email)
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() =>
                  conversationId &&
                  statusMutation.mutate({ conversationId, status: 'resolved', notifyUser: true })
                }
              >
                <Mail className="mr-2 h-4 w-4" /> Resolve &amp; notify user
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">
        {/* Messages column */}
        <Card className="flex flex-col" style={{ height: 'calc(100vh - 200px)' }}>
          <div
            ref={scrollRef}
            data-testid="feedback-message-list"
            className="flex-1 min-h-0 overflow-y-auto p-4"
          >
            <div className="space-y-3">
              {messages.map((msg) => {
                const isAdmin = msg.sender_role === 'admin'
                const isSystem = msg.sender_role === 'system'

                if (isSystem) {
                  return (
                    <div key={msg.id} className="flex justify-center py-1">
                      <span className="text-xs text-muted-foreground italic">{msg.body}</span>
                    </div>
                  )
                }

                return (
                  <div
                    key={msg.id}
                    className={cn(
                      'flex flex-col gap-0.5 max-w-[80%]',
                      isAdmin ? 'ml-auto items-end' : 'mr-auto items-start',
                    )}
                  >
                    <span className="text-[10px] text-muted-foreground px-1">
                      {isAdmin ? formatAdminSenderLabel(msg) : conversation.profiles?.display_name ?? 'User'}
                    </span>
                    <div
                      className={cn(
                        'rounded-xl px-3 py-2 text-sm',
                        isAdmin
                          ? 'bg-primary text-primary-foreground rounded-br-sm'
                          : 'bg-muted rounded-bl-sm',
                      )}
                    >
                      {msg.body && <p className="whitespace-pre-wrap break-words">{msg.body}</p>}

                      {msg.attachments?.length > 0 && (
                        <div className="mt-1.5 space-y-1">
                          {(msg.attachments as Array<Record<string, unknown>>).map((att, i) => (
                            <div key={i}>
                              {att.type === 'screenshot' && (
                                <a href={att.url as string} target="_blank" rel="noopener noreferrer">
                                  <img
                                    src={att.url as string}
                                    alt={att.filename as string}
                                    className="max-w-full rounded-md border max-h-48 object-cover"
                                  />
                                </a>
                              )}
                              {att.type === 'screen_recording' && (
                                <video
                                  src={att.url as string}
                                  controls
                                  className="max-w-full rounded-md max-h-48"
                                />
                              )}
                              {att.type === 'audio' && (
                                <audio src={att.url as string} controls className="max-w-full" />
                              )}
                              {att.type === 'file' && (
                                <a
                                  href={att.url as string}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="underline text-xs"
                                >
                                  {att.filename as string}
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <span className="text-[10px] text-muted-foreground px-1">
                      {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true })}
                      {isAdmin &&
                        msg.id === lastAdminMessageId &&
                        conversation.user_last_read_at &&
                        new Date(conversation.user_last_read_at) >= new Date(msg.created_at) && (
                          <> · Seen {formatDistanceToNow(new Date(conversation.user_last_read_at), { addSuffix: true })}</>
                        )}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Reply input */}
          <div className="border-t p-3">
            <div className="flex gap-2 items-end">
              <Textarea
                ref={textareaRef}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type a reply… (Cmd/Ctrl+Enter to send)"
                className="min-h-[40px] max-h-[320px] resize-y overflow-y-auto text-sm leading-relaxed"
                rows={2}
              />
              <Button
                size="icon"
                onClick={handleSendReply}
                disabled={!replyText.trim() || replyMutation.isPending}
              >
                {replyMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </Card>

        {/* Sidebar — context + controls */}
        <div className="space-y-4">
          {/* Status + Priority controls */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Controls</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground">Status</label>
                <Select
                  value={conversation.status}
                  onValueChange={(value) => {
                    if (conversationId) {
                      statusMutation.mutate({
                        conversationId,
                        status: value as ConversationStatus,
                      })
                    }
                  }}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="waiting_on_user">Waiting on User</SelectItem>
                    <SelectItem value="waiting_on_admin">Waiting on Admin</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs text-muted-foreground">Priority</label>
                <Select
                  value={conversation.priority}
                  onValueChange={(value) => {
                    if (conversationId) {
                      priorityMutation.mutate({
                        conversationId,
                        priority: value as ConversationPriority,
                      })
                    }
                  }}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Conversation info */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Category</span>
                <CategoryBadge category={conversation.category} />
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status</span>
                <StatusBadge status={conversation.status} />
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Priority</span>
                <PriorityBadge priority={conversation.priority} />
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="text-muted-foreground">User</span>
                <span className="font-medium truncate ml-2">
                  {conversation.profiles?.display_name ?? conversation.user_email}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Created</span>
                <span>{format(new Date(conversation.created_at), 'MMM d, HH:mm')}</span>
              </div>
              {conversation.last_message_at && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Last message</span>
                  <span>
                    {formatDistanceToNow(new Date(conversation.last_message_at), { addSuffix: true })}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Context snapshot */}
          {context && Object.keys(context).length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Context</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 text-xs">
                {/* What the user was looking at. Present only when the form was
                    opened from a panel, so its absence is meaningful too. */}
                {!!context.target && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">About</span>
                    <span className="font-medium truncate ml-2">{String(context.target)}</span>
                  </div>
                )}
                {!!context.page_url && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Page</span>
                    <span className="font-mono truncate ml-2">{String(context.page_url)}</span>
                  </div>
                )}
                {!!context.layout_id && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Layout</span>
                    <span className="font-mono truncate ml-2 max-w-[140px]">
                      {String(context.layout_name || '') || String(context.layout_id).slice(0, 12)}
                    </span>
                  </div>
                )}
                {context.building_count !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Buildings</span>
                    <span>{Number(context.building_count)}</span>
                  </div>
                )}
                {context.item_count !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Items</span>
                    <span>{Number(context.item_count)}</span>
                  </div>
                )}
                {!!context.viewport && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Viewport</span>
                    <span className="font-mono">{String(context.viewport)}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
